import { Injectable, inject, signal } from '@angular/core';
import {
  GetVisitorIdQueryService,
  LogoutMutationService,
  RefreshTokenMutationService,
} from '@shared/graphql/generated/api.generated';
import { constantVariables } from '@shared/helper/configs';
import { IAccessTokenPayload } from '@shared/models/access-token.interface';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, take, tap } from 'rxjs';
import { GeneralService } from './general.service';
import { envs } from '@envs/environment';
import { PushNotificationService } from './push-notification.service';
import { deauthorizeUser } from '@metrixorg/websdk';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  userInfo = signal<IAccessTokenPayload | undefined>(undefined);

  generalService = inject(GeneralService);
  private readonly refreshTokenMutationService = inject(RefreshTokenMutationService);
  private readonly logoutMutationService = inject(LogoutMutationService);
  private readonly getVisitorIdQueryService = inject(GetVisitorIdQueryService);
  private readonly pushNotificationService = inject(PushNotificationService);

  constructor() {
    const atk = this.getAtk();
    if (atk) {
      try {
        const payload: any = jwtDecode(atk);
        let activeSubscription = null;
        if (payload.as) {
          activeSubscription = {
            expiresOn: payload.as.ed,
            name: payload.as.pn,
            type: payload.as.pt,
          };
        }
        const user: IAccessTokenPayload = {
          id: payload.sub,
          firstName: payload.fn,
          lastName: payload.ln,
          did: payload.vid,
          email: payload.e,
          roles: payload.rs,
          mobile: payload.m,
          gender: payload.g,
          activeSubscription: activeSubscription,
        };
        this.userInfo.set(user);
      } catch (error) {
        this.userInfo.set(undefined);
      }
    }
  }

  getAtk(): string | null {
    if (this.generalService.isBrowserOnly) {
      return localStorage.getItem(constantVariables.accessToken);
    }
    return null;
  }

  getRtk(): string | null {
    if (this.generalService.isBrowserOnly) {
      return localStorage.getItem(constantVariables.refreshToken);
    }
    return null;
  }

  setUserInfo(atk: string, rtk: string) {
    localStorage.setItem(constantVariables.accessToken, atk);
    localStorage.setItem(constantVariables.refreshToken, rtk);
    const payload: any = jwtDecode(atk);
    let activeSubscription = null;
    if (payload.as) {
      activeSubscription = {
        expiresOn: payload.as.ed,
        name: payload.as.pn,
        type: payload.as.pt,
      };
    }
    const user: IAccessTokenPayload = {
      id: payload.sub,
      did: payload.vid,
      firstName: payload.fn,
      lastName: payload.ln,
      email: payload.e,
      roles: payload.rs,
      mobile: payload.m,
      gender: payload.g,
      activeSubscription: activeSubscription,
    };
    this.userInfo.set(user);
  }

  refreshToken() {
    return this.refreshTokenMutationService.mutate().pipe(
      tap((res) => {
        this.setUserInfo(
          res.data?.refreshToken?.access_token!,
          res.data?.refreshToken?.refresh_token!,
        );
        return res;
      }),
      catchError((e) => {
        this.logout().subscribe();
        throw e;
      }),
    );
  }

  logout(returnUrl?: string) {
    this.pushNotificationService.unsubscribe();
    return this.logoutMutationService.mutate().pipe(
      take(1),
      map((res) => {
        this.logoutFunction(returnUrl);
        return res;
      }),
      catchError((e) => {
        this.logoutFunction(returnUrl);
        throw e;
      }),
    );
  }

  private logoutFunction(returnUrl?: string) {
    if (this.generalService.isBrowserOnly) {
      deauthorizeUser();
      localStorage.removeItem(constantVariables.visitorId);
      localStorage.removeItem(constantVariables.accessToken);
      localStorage.removeItem(constantVariables.refreshToken);
      this.userInfo.set(undefined);
      this.getVisitorId().subscribe();
      window.location.href = `${envs.gameWebsiteBaseUrl}/auth/logout?ru=${returnUrl ?? '/'}`;
    }
  }

  getVisitorId() {
    return this.getVisitorIdQueryService.fetch().pipe(
      map((res) => {
        if (this.generalService.isBrowserOnly) {
          localStorage.setItem(constantVariables.visitorId, res.data.getVisitorId);
        }
        return res;
      }),
    );
  }
}
